/**
 * Auto Apply filters: the shape the search, the apply and the saved settings
 * share (docs/auto-apply-v2.md), and the small pure helpers the page needs
 * around it: a stable key, the bar's value, chips, suggestions, summaries.
 */

import { humanise } from '@/lib/utils'
import type { AutoApplyFilters, AutoApplySuggestion, JobFilterChoices, WorkplaceType } from '@/types/api'
import { countryLabel, placeLabel, type JobFilters } from '../JobFilterBar'

/** The keys the Auto Apply API takes, in the order chips are shown. */
export const AUTO_APPLY_KEYS = [
  'q',
  'workplaceTypes',
  'locations',
  'remoteIn',
  'companies',
  'excludeCompanies',
  'roles',
  'seniorities',
  'employmentTypes',
  'postedWithinDays',
  'maxExperienceYears',
  'sponsorsVisa',
  'minMatchScore',
  'sources',
  'boards',
] as const satisfies ReadonlyArray<keyof AutoApplyFilters>

/** When the user has no minimum of their own. */
export const DEFAULT_MIN_SCORE = 60

/** Drop empty values so equal filter sets compare (and cache) equal. */
export function cleanFilters(filters: AutoApplyFilters): AutoApplyFilters {
  const out: Record<string, unknown> = {}
  for (const key of AUTO_APPLY_KEYS) {
    const value = filters[key]
    if (value === undefined || value === null || value === '' || value === false) continue
    if (Array.isArray(value)) {
      if (value.length) out[key] = [...value]
      continue
    }
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed) out[key] = trimmed
      continue
    }
    out[key] = value
  }
  return out as AutoApplyFilters
}

/** Order-insensitive key for caching and "is this the saved set?". */
export function filtersKey(filters: AutoApplyFilters | null | undefined): string {
  const clean = cleanFilters(filters ?? {})
  const sorted: Record<string, unknown> = {}
  for (const key of AUTO_APPLY_KEYS) {
    const value = clean[key]
    if (value === undefined) continue
    sorted[key] = Array.isArray(value) ? [...value].sort() : value
  }
  return JSON.stringify(sorted)
}

/** The JobFilterBar edits the shared job filters; these are the ones it shows here. */
export function toBarValue(filters: AutoApplyFilters): JobFilters {
  const { minMatchScore: _score, ...rest } = filters
  return rest
}

/** Back from the bar: keep only what the Auto Apply API takes, plus our own match score. */
export function fromBarValue(next: JobFilters, current: AutoApplyFilters): AutoApplyFilters {
  return cleanFilters({
    q: next.q,
    companies: next.companies,
    excludeCompanies: next.excludeCompanies,
    locations: next.locations,
    remoteIn: next.remoteIn,
    workplaceTypes: next.workplaceTypes as WorkplaceType[] | undefined,
    employmentTypes: next.employmentTypes,
    roles: next.roles,
    seniorities: next.seniorities,
    postedWithinDays: next.postedWithinDays,
    maxExperienceYears: next.maxExperienceYears,
    sponsorsVisa: next.sponsorsVisa,
    sources: next.sources,
    boards: next.boards,
    minMatchScore: current.minMatchScore,
  })
}

/** A suggestion's `change` merged in and its `remove` keys dropped. */
export function applySuggestion(filters: AutoApplyFilters, suggestion: AutoApplySuggestion): AutoApplyFilters {
  const next: AutoApplyFilters = { ...filters, ...suggestion.change }
  for (const key of suggestion.remove ?? []) delete next[key]
  return cleanFilters(next)
}

const WORKPLACE_LABELS: Record<string, string> = { remote: 'Remote', hybrid: 'Hybrid', onsite: 'Office' }
const EMPLOYMENT_LABELS: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  internship: 'Internship',
  temporary: 'Temporary',
}

export const workplaceLabel = (value: string | null | undefined): string =>
  value ? WORKPLACE_LABELS[value.toLowerCase()] ?? humanise(value) : ''

/** A source or portal key as people read it. */
export function sourceLabel(key: string, labels?: Record<string, string>): string {
  if (labels?.[key]) return labels[key]!
  if (key === 'internal') return 'Company careers'
  if (key === 'manual') return 'Your links'
  if (key === 'naukri') return 'Naukri'
  if (key === 'linkedin') return 'LinkedIn'
  return humanise(key)
}

const postedLabel = (days: number) => (days === 1 ? 'Last 24 hours' : `Last ${days} days`)

/** One removable chip per filter value. `remove` returns the filters without it. */
export interface FilterChipItem {
  id: string
  label: string
  remove: (filters: AutoApplyFilters) => AutoApplyFilters
}

const without = <K extends keyof AutoApplyFilters>(key: K) => (filters: AutoApplyFilters) => {
  const next = { ...filters }
  delete next[key]
  return next
}

const withoutItem =
  <K extends keyof AutoApplyFilters>(key: K, item: string) =>
  (filters: AutoApplyFilters) => {
    const list = ((filters[key] as string[] | undefined) ?? []).filter((v) => v !== item)
    const next: AutoApplyFilters = { ...filters, [key]: list.length ? list : undefined }
    // Remote-in rides on a place: without places it means nothing.
    if (key === 'locations' && !list.length) delete next.remoteIn
    return cleanFilters(next)
  }

export interface ChipOptions {
  choices?: JobFilterChoices
  sourceLabels?: Record<string, string>
  /** The user's own minimum: no chip when the filter just repeats it. */
  defaultScore?: number
}

export function filterChips(filters: AutoApplyFilters, options: ChipOptions = {}): FilterChipItem[] {
  const chips: FilterChipItem[] = []
  const labelOf = (list: Array<{ value: string; label: string }> | undefined, value: string) =>
    list?.find((o) => o.value === value)?.label ?? humanise(value)

  if (filters.q) chips.push({ id: 'q', label: `“${filters.q}”`, remove: without('q') })
  for (const w of filters.workplaceTypes ?? [])
    chips.push({ id: `w:${w}`, label: workplaceLabel(w), remove: withoutItem('workplaceTypes', w) })
  for (const place of filters.locations ?? [])
    chips.push({ id: `l:${place}`, label: placeLabel(place), remove: withoutItem('locations', place) })
  if (filters.remoteIn)
    chips.push({ id: 'remoteIn', label: `Remote in ${countryLabel(filters.remoteIn)}`, remove: without('remoteIn') })
  for (const c of filters.companies ?? [])
    chips.push({ id: `c:${c}`, label: `Only ${c}`, remove: withoutItem('companies', c) })
  for (const c of filters.excludeCompanies ?? [])
    chips.push({ id: `x:${c}`, label: `Hide ${c}`, remove: withoutItem('excludeCompanies', c) })
  for (const r of filters.roles ?? [])
    chips.push({ id: `r:${r}`, label: labelOf(options.choices?.roles, r), remove: withoutItem('roles', r) })
  for (const s of filters.seniorities ?? [])
    chips.push({ id: `s:${s}`, label: labelOf(options.choices?.seniorities, s), remove: withoutItem('seniorities', s) })
  for (const e of filters.employmentTypes ?? [])
    chips.push({ id: `e:${e}`, label: EMPLOYMENT_LABELS[e] ?? humanise(e), remove: withoutItem('employmentTypes', e) })
  if (filters.postedWithinDays)
    chips.push({ id: 'posted', label: postedLabel(filters.postedWithinDays), remove: without('postedWithinDays') })
  if (filters.maxExperienceYears)
    chips.push({ id: 'exp', label: `Up to ${filters.maxExperienceYears} yrs experience`, remove: without('maxExperienceYears') })
  if (filters.sponsorsVisa) chips.push({ id: 'visa', label: 'Sponsors visa', remove: without('sponsorsVisa') })
  if (filters.minMatchScore != null && filters.minMatchScore !== options.defaultScore)
    chips.push({ id: 'score', label: `${filters.minMatchScore}%+ match`, remove: without('minMatchScore') })
  for (const s of filters.sources ?? [])
    chips.push({ id: `src:${s}`, label: sourceLabel(s, options.sourceLabels), remove: withoutItem('sources', s) })
  for (const b of filters.boards ?? [])
    chips.push({ id: `b:${b}`, label: sourceLabel(b, options.sourceLabels), remove: withoutItem('boards', b) })
  return chips
}

/** "Remote · Bengaluru · 70+": what background Auto Apply follows. */
export function summarizeFilters(
  filters: AutoApplyFilters | null | undefined,
  fallbackScore: number,
  options: ChipOptions = {},
): string {
  const clean = cleanFilters(filters ?? {})
  const parts = filterChips({ ...clean, minMatchScore: undefined }, options).map((chip) => chip.label)
  const score = clean.minMatchScore ?? fallbackScore
  if (!parts.length) return `Every match at ${score}%+`
  const shown = parts.slice(0, 4)
  if (parts.length > 4) shown.push(`+${parts.length - 4} more`)
  shown.push(`${score}%+`)
  return shown.join(' · ')
}

/** "Queued 48 · 2 skipped (daily limit)". */
export function applyResultText(queued: number, skipped: Array<{ reason: string }>): { title: string; detail?: string } {
  if (!skipped.length) return { title: `Queued ${queued} application${queued === 1 ? '' : 's'}` }
  const reasons = [...new Set(skipped.map((s) => humanise(s.reason).toLowerCase()).filter(Boolean))]
  const why = reasons.length ? ` (${reasons.slice(0, 2).join(', ')}${reasons.length > 2 ? ', …' : ''})` : ''
  return { title: `Queued ${queued} · ${skipped.length} skipped${why}` }
}

export const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value)
